import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';

interface CookieConsentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CookieConsentModal({
  isOpen,
  onClose,
}: CookieConsentModalProps) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        className="bg-white dark:bg-slate-900 rounded-lg shadow-2xl max-h-[90vh] w-full max-w-3xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-900">
          <h2 id="modal-title" className="text-2xl font-bold text-wine">
            Política de Privacidade e Cookies
          </h2>
          <button
            onClick={onClose}
            aria-label="Fechar modal"
            className="text-slate-500 hover:text-slate-900 dark:hover:text-slate-100 transition flex-shrink-0"
          >
            <X size={24} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 p-6 text-sm text-slate-700 dark:text-slate-300 space-y-4">
          <p className="text-xs text-slate-600 dark:text-slate-400 italic">
            <strong>Última atualização:</strong> 18 de agosto de 2026
          </p>

          <p>
            O <strong>Lily Book Club</strong> valoriza a privacidade e a
            proteção dos dados pessoais de seus clientes e visitantes.
          </p>

          <p>
            Esta Política de Privacidade e Cookies explica quais dados pessoais
            podem ser coletados durante a utilização do nosso site e checkout,
            como essas informações são utilizadas, com quem podem ser
            compartilhadas e quais são os direitos dos titulares, nos termos da{' '}
            <strong>
              Lei nº 13.709/2018 — Lei Geral de Proteção de Dados Pessoais
              (LGPD)
            </strong>
            .
          </p>

          <section className="space-y-3">
            <h3 className="text-base font-bold text-wine">1. Quem somos</h3>
            <p>
              O <strong>Lily Book Club</strong> é uma plataforma voltada à venda
              e organização de produtos, inscrições e experiências relacionadas
              a clubes do livro e eventos.
            </p>
            <p>
              Para dúvidas, solicitações ou assuntos relacionados à privacidade
              e proteção de dados:
              <br />
              <strong>E-mail:</strong>{' '}
              <a
                href="mailto:lilybookclubb@gmail.com"
                className="text-wine hover:underline"
              >
                lilybookclubb@gmail.com
              </a>
              <br />
              <strong>Localização:</strong> Manaus, Amazonas — Brasil.
            </p>
          </section>

          <section className="space-y-3">
            <h3 className="text-base font-bold text-wine">
              2. Quais dados coletamos
            </h3>
            <p>
              Apenas coletamos os dados necessários para fornecer nossos
              produtos e serviços, processar compras, realizar inscrições,
              entrar em contato com o cliente e manter a segurança da
              plataforma.
            </p>
            <p>Dependendo da compra ou serviço utilizado, podemos coletar:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Nome</li>
              <li>Endereço de e-mail</li>
              <li>Número de telefone, quando solicitado</li>
              <li>Informações necessárias para inscrição em eventos</li>
              <li>Informações relacionadas ao produto ou serviço adquirido</li>
              <li>
                Informações relacionadas ao pagamento e status da transação
              </li>
              <li>Dados técnicos do dispositivo e navegador</li>
              <li>Endereço IP</li>
              <li>Informações de acesso e navegação</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h3 className="text-base font-bold text-wine">
              3. Dados de cartão
            </h3>
            <p>
              O Lily Book Club{' '}
              <strong>
                não armazena os dados completos do cartão de crédito
              </strong>{' '}
              em seus próprios bancos de dados. Os dados necessários para
              processar a transação podem ser tratados diretamente pelo provedor
              de pagamento, como o <strong>Asaas</strong>, de acordo com suas
              próprias políticas e requisitos de segurança.
            </p>
          </section>

          <section className="space-y-3">
            <h3 className="text-base font-bold text-wine">4. Cookies</h3>
            <p>
              Cookies são pequenos arquivos armazenados no dispositivo utilizado
              para acessar o site.
            </p>
            <div className="ml-4 space-y-2">
              <div>
                <strong className="text-wine">4.1. Cookies essenciais</strong>
                <p className="text-xs mt-1">
                  São utilizados para permitir o funcionamento adequado do site
                  e do checkout (segurança, funcionamento do checkout, prevenção
                  de fraudes).
                </p>
              </div>
              <div>
                <strong className="text-wine">4.2. Cookies de análise</strong>
                <p className="text-xs mt-1">
                  Utilizamos o <strong>Google Analytics</strong> para
                  compreender como os visitantes utilizam nosso site. Esses
                  cookies poderão ser utilizados somente quando houver
                  consentimento.
                </p>
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-base font-bold text-wine">
              5. Como utilizamos seus dados
            </h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Processamento de compras:</strong> Identificar o
                comprador, registrar a compra, confirmar o pedido
              </li>
              <li>
                <strong>Processamento de pagamentos:</strong> Enviar informações
                ao provedor de pagamento, confirmar pagamentos
              </li>
              <li>
                <strong>Comunicação:</strong> Confirmações, informações sobre
                produtos e eventos, atendimento ao cliente
              </li>
              <li>
                <strong>Segurança:</strong> Detectar atividades suspeitas,
                prevenir fraudes, proteger infraestrutura
              </li>
              <li>
                <strong>Melhoria do serviço:</strong> Compreender como nosso
                site é utilizado
              </li>
            </ul>
          </section>

          <section className="space-y-3">
            <h3 className="text-base font-bold text-wine">
              6. Compartilhamento de dados
            </h3>
            <p>
              O Lily Book Club{' '}
              <strong>não vende ou comercializa dados pessoais</strong> de seus
              clientes. Podemos compartilhar dados com prestadores de serviços
              necessários:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Asaas:</strong> Processamento de pagamentos
              </li>
              <li>
                <strong>Resend:</strong> Envio de e-mails relacionados às
                compras e inscrições
              </li>
              <li>
                <strong>Google Analytics:</strong> Análise estatística e
                compreensão do uso do site
              </li>
              <li>
                <strong>Fornecedores de infraestrutura:</strong> Hospedagem,
                banco de dados, armazenamento
              </li>
            </ul>
          </section>

          <section className="space-y-3">
            <h3 className="text-base font-bold text-wine">7. Segurança</h3>
            <p>
              Adotamos medidas técnicas para proteger os dados pessoais contra
              acessos não autorizados, utilizando HTTPS/TLS, controle de acesso,
              monitoramento de eventos e armazenamento seguro das informações.
              Apesar das medidas adotadas, nenhum sistema conectado à Internet
              pode ser considerado completamente seguro.
            </p>
          </section>

          <section className="space-y-3">
            <h3 className="text-base font-bold text-wine">
              8. Seus direitos (LGPD)
            </h3>
            <p>
              Você poderá exercer direitos relacionados aos seus dados pessoais,
              incluindo:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Solicitar acesso aos dados</li>
              <li>Solicitar correção de dados incorretos</li>
              <li>Solicitar eliminação ou anonimização</li>
              <li>Solicitar portabilidade dos dados</li>
              <li>Revogar consentimentos anteriormente fornecidos</li>
              <li>Opor-se a determinados tratamentos</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h3 className="text-base font-bold text-wine">
              9. Como exercer seus direitos
            </h3>
            <p>
              Para exercer seus direitos ou esclarecer dúvidas sobre o
              tratamento de seus dados:
            </p>
            <div className="bg-wine/10 dark:bg-wine/20 rounded-lg p-3">
              <p className="font-semibold text-wine">
                📧 lilybookclubb@gmail.com
              </p>
              <p className="text-xs mt-2">Manaus, Amazonas — Brasil</p>
            </div>
          </section>

          <hr className="my-4 border-slate-200 dark:border-slate-700" />
          <p className="text-xs text-slate-600 dark:text-slate-400 text-center">
            © 2026 Lily Book Club. Todos os direitos reservados.
          </p>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-slate-200 dark:border-slate-700 sticky bottom-0 bg-white dark:bg-slate-900">
          <Button variant="wine" className="flex-1" onClick={onClose}>
            Entendi
          </Button>
        </div>
      </div>
    </div>
  );
}
